import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, ArrowLeft, CheckCircle, Pill, MessageCircle, Phone } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../utils/analytics';

const QUESTIONS = [
  {
    id: 'goal',
    question: "What's your primary goal?",
    options: [
      { value: 'weight-only', label: 'Weight loss only' },
      { value: 'weight-energy', label: 'Weight loss + energy' },
      { value: 'weight-blood-sugar', label: 'Weight loss + blood sugar management' }
    ]
  },
  {
    id: 'experience',
    question: 'Have you tried a GLP-1 medication before?',
    options: [
      { value: 'no', label: 'No, this is my first time' },
      { value: 'semaglutide', label: 'Yes, semaglutide' },
      { value: 'tirzepatide', label: 'Yes, tirzepatide' },
      { value: 'both', label: 'Yes, both' }
    ]
  },
  {
    id: 'cost',
    question: 'How important is cost to you?',
    options: [
      { value: 'very', label: 'Very important — budget is a priority' },
      { value: 'somewhat', label: 'Somewhat important' },
      { value: 'less', label: 'Less important — I want the strongest option' }
    ]
  },
  {
    id: 'sensitivity',
    question: 'How sensitive are you to medication side effects?',
    options: [
      { value: 'very', label: 'Very sensitive — I react to most medications' },
      { value: 'somewhat', label: 'Somewhat sensitive' },
      { value: 'not', label: 'Not usually sensitive' }
    ]
  },
  {
    id: 'weight',
    question: 'How much weight are you looking to lose?',
    options: [
      { value: 'under20', label: 'Under 20 lbs' },
      { value: '20-40', label: '20-40 lbs' },
      { value: '40-60', label: '40-60 lbs' },
      { value: '60+', label: '60+ lbs' }
    ]
  },
  {
    id: 'priority',
    question: 'What matters most to you?',
    options: [
      { value: 'fastest', label: 'Fastest results possible' },
      { value: 'affordable', label: 'Most affordable option' },
      { value: 'tolerated', label: 'Best tolerated / fewest side effects' },
      { value: 'proven', label: 'Most proven track record' }
    ]
  }
];

const MedicationMatch = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    if (currentStep < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 300);
    } else {
      trackEvent('medication_match_completed', answers);
      setTimeout(() => setShowResults(true), 300);
    }
  };

  const getRecommendation = () => {
    let tirzepatideScore = 0;
    let semaglutideScore = 0;
    const reasons = { tirzepatide: [], semaglutide: [] };

    // Goal analysis
    if (answers.goal === 'weight-blood-sugar') {
      tirzepatideScore += 2;
      reasons.tirzepatide.push("Tirzepatide's dual GIP/GLP-1 mechanism provides additional blood sugar benefits");
    }
    if (answers.goal === 'weight-energy') {
      tirzepatideScore += 1;
      reasons.tirzepatide.push("Tirzepatide users often report better energy levels at higher doses");
    }

    // Experience analysis
    if (answers.experience === 'semaglutide') {
      tirzepatideScore += 2;
      reasons.tirzepatide.push("You've already tried semaglutide — tirzepatide offers a different mechanism");
    }
    if (answers.experience === 'no') {
      semaglutideScore += 1;
      reasons.semaglutide.push("Semaglutide has a longer track record for first-time GLP-1 users");
    }

    // Cost analysis
    if (answers.cost === 'very') {
      semaglutideScore += 2;
      reasons.semaglutide.push("Compounded semaglutide is typically the most affordable option");
    }
    if (answers.cost === 'less') {
      tirzepatideScore += 1;
      reasons.tirzepatide.push("You prioritize results over cost — tirzepatide shows stronger average outcomes");
    }

    // Sensitivity analysis
    if (answers.sensitivity === 'very') {
      semaglutideScore += 1;
      reasons.semaglutide.push("Semaglutide has a well-established side effect profile you can titrate slowly");
    }
    if (answers.sensitivity === 'not') {
      tirzepatideScore += 1;
      reasons.tirzepatide.push("You're not particularly sensitive — you can likely tolerate tirzepatide's stronger effects");
    }

    // Weight analysis
    if (answers.weight === '60+' || answers.weight === '40-60') {
      tirzepatideScore += 2;
      reasons.tirzepatide.push("For significant weight loss goals, tirzepatide shows higher average results in clinical data");
    }
    if (answers.weight === 'under20') {
      semaglutideScore += 1;
      reasons.semaglutide.push("For modest weight loss goals, semaglutide is often sufficient and more affordable");
    }

    // Priority analysis
    if (answers.priority === 'fastest') {
      tirzepatideScore += 2;
      reasons.tirzepatide.push("Tirzepatide clinical trials showed faster average weight loss than semaglutide");
    }
    if (answers.priority === 'affordable') {
      semaglutideScore += 2;
      reasons.semaglutide.push("Compounded semaglutide programs are typically the most budget-friendly option");
    }
    if (answers.priority === 'tolerated') {
      semaglutideScore += 1;
      reasons.semaglutide.push("Semaglutide's longer market history means more established dosing strategies for tolerability");
    }
    if (answers.priority === 'proven') {
      semaglutideScore += 1;
      reasons.semaglutide.push("Semaglutide has more years of real-world data and clinical experience");
    }

    const recommendation = tirzepatideScore > semaglutideScore ? 'tirzepatide' : 'semaglutide';
    const selectedReasons = reasons[recommendation].slice(0, 3);
    
    // Add default reasons if needed
    if (selectedReasons.length < 3) {
      if (recommendation === 'tirzepatide') {
        if (!selectedReasons.some(r => r.includes('dual'))) {
          selectedReasons.push("Tirzepatide's dual receptor mechanism provides enhanced metabolic benefits");
        }
        if (selectedReasons.length < 3) {
          selectedReasons.push("Clinical data shows 20-22% average weight loss at higher doses");
        }
      } else {
        if (!selectedReasons.some(r => r.includes('affordable'))) {
          selectedReasons.push("Compounded semaglutide offers excellent value for most people");
        }
        if (selectedReasons.length < 3) {
          selectedReasons.push("Well-established medication with predictable results and dosing");
        }
      }
    }

    return { medication: recommendation, reasons: selectedReasons.slice(0, 3) };
  };

  const handleForecastClick = () => {
    trackEvent('medication_match_to_forecast', { recommendation: getRecommendation().medication });
    navigate('/calculator');
  };

  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;

  if (showResults) {
    const { medication, reasons } = getRecommendation();
    const isTirzepatide = medication === 'tirzepatide';

    return (
      <div className="min-h-screen bg-[#0B1020] text-white pb-32">
        <SEOHead 
          title="Your Medication Match | Weight Loss Care Team"
          description="Get a personalized GLP-1 medication recommendation based on your goals and preferences."
          path="/medication-match"
        />

        <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Your Recommendation</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 -mt-4">
          {/* Recommendation Card */}
          <Card className={`border-2 p-6 mb-6 ${
            isTirzepatide 
              ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-purple-500/50' 
              : 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/50'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <Pill className={`w-8 h-8 ${isTirzepatide ? 'text-purple-400' : 'text-blue-400'}`} />
              <h2 className="text-xl font-bold capitalize">{medication}</h2>
            </div>
            
            <p className="text-lg mb-6">
              {isTirzepatide 
                ? "Based on your profile, Tirzepatide is likely the stronger fit for you."
                : "Based on your profile, Semaglutide is likely the better starting point for you."
              }
            </p>

            <div className="space-y-3">
              <p className="text-sm text-[#94A3B8] font-medium">Why this recommendation:</p>
              {reasons.map((reason, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-[#0B1020]/50 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#E2E8F0]">{reason}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Disclaimer */}
          <Card className="bg-[#1E293B]/50 border-[#334155] p-4 mb-6">
            <p className="text-[#94A3B8] text-sm text-center italic">
              This is a general estimate — not medical advice. The best way to know is to talk through your specific situation.
            </p>
          </Card>

          {/* CTAs */}
          <div className="space-y-3">
            <Button
              onClick={handleForecastClick}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-6 text-lg font-semibold rounded-xl"
              data-testid="see-forecast-cta"
            >
              See Your 90-Day Forecast
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>

            <a 
              href="sms:8016518363"
              className="flex items-center justify-center gap-2 w-full bg-[#1E293B] hover:bg-[#334155] text-white py-4 text-lg font-semibold rounded-xl border border-[#334155] transition-colors"
              data-testid="text-bryan-cta"
            >
              <MessageCircle className="w-5 h-5" />
              Text Bryan
            </a>
          </div>

          {/* Restart */}
          <button 
            onClick={() => { setShowResults(false); setCurrentStep(0); setAnswers({}); }}
            className="w-full text-center text-[#94A3B8] hover:text-white mt-6 py-2"
          >
            ← Start Over
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = QUESTIONS[currentStep];

  return (
    <div className="min-h-screen bg-[#0B1020] text-white pb-32">
      <SEOHead 
        title="Medication Match Tool | Weight Loss Care Team"
        description="Find out which GLP-1 medication might be right for you based on your goals, preferences, and situation."
        path="/medication-match"
      />

      {/* Header */}
      <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/" className="text-[#94A3B8] hover:text-white text-sm mb-4 inline-block">
            ← Back to Home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Pill className="w-8 h-8 text-[#3B82F6]" />
            <h1 className="text-2xl sm:text-3xl font-bold">Medication Match</h1>
          </div>
          <p className="text-[#94A3B8] text-base sm:text-lg">
            Answer 6 quick questions to see which medication might fit your situation
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[#94A3B8] mb-2">
            <span>Question {currentStep + 1} of {QUESTIONS.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 bg-[#1E293B] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#3B82F6] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6">
          <h2 className="text-xl font-semibold mb-6">{currentQuestion.question}</h2>
          
          <div className="space-y-3">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQuestion.id, option.value)}
                className={`w-full text-left p-4 rounded-lg border transition-all ${
                  answers[currentQuestion.id] === option.value
                    ? 'bg-[#3B82F6] border-[#3B82F6] text-white'
                    : 'bg-[#0B1020] border-[#334155] text-[#E2E8F0] hover:border-[#3B82F6]/50'
                }`}
                data-testid={`option-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Back Button */}
        {currentStep > 0 && (
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous question
          </button>
        )}
      </div>
    </div>
  );
};

export default MedicationMatch;
